"use client"

import { useEffect, useMemo } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { WishlistIdsResponse } from "@/generated/api/types.gen"
import { wishlistGateway } from "@/platform/api/gateways/wishlist"
import { useStateInvalidation } from "@/platform/app-mode/state-context"
import { logger } from "@/platform/observability/logger"
import { invalidateDomain } from "@/platform/session/private-keys"
import { useGateway } from "@/platform/session/use-api"
import { usePrivateMutation } from "@/platform/session/use-private-mutation"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useViewer } from "@/platform/session/use-viewer"
import { useViewerBoundary } from "@/platform/session/use-viewer-boundary"
import { ANONYMOUS_VIEWER, privateKey } from "@/platform/session/viewer"
import { holdViewerBoundary } from "@/platform/session/viewer-boundary"
import { ensureWishlistId, getStoredWishlist, setStoredWishlist, syncWishlistOwnership, useWishlistId } from "@/platform/shopper/use-wishlist-id"
import { WISHLIST_PRODUCTS } from "@/platform/shopper/use-wishlist-products"

const WISHLIST = "wishlist"

const idsParts = (wishlistId: string | null) => [WISHLIST, "ids", wishlistId] as const

const emptyIds: string[] = []

/** Applies a save/remove to the cached id set, deduping. Pure so it's unit-testable. */
const nextWishlistIds = (current: string[], productId: string, saved: boolean): string[] => {
  const ids = new Set(current)
  if (saved) ids.add(productId)
  else ids.delete(productId)
  return [...ids]
}

export function useWishlistIds() {
  const wishlistId = useWishlistId()
  const viewer = useViewer()
  const generation = useViewerBoundary()
  const owner = viewer === null || viewer === ANONYMOUS_VIEWER ? null : viewer
  const queryClient = useQueryClient()
  const wishlists = useGateway(wishlistGateway)

  useEffect(() => {
    if (viewer === null) return
    syncWishlistOwnership(owner, wishlists)
      .then((changed) => {
        if (changed) void invalidateDomain(queryClient, WISHLIST, WISHLIST_PRODUCTS)
      })
      .catch((error) => logger.error("Wishlist ownership sync failed", {}, error as Error))
  }, [owner, viewer, generation, queryClient, wishlists])

  const { data, ...rest } = usePrivateQuery(
    {
      queryKey: idsParts(wishlistId),
      queryFn: async () => {
        const held = holdViewerBoundary()
        const ids = wishlistId ? await wishlists.ids(wishlistId) : null
        if (ids) return ids
        if (held() && getStoredWishlist()?.id === wishlistId) setStoredWishlist(null)
        return { product_ids: emptyIds }
      },
      enabled: !!wishlistId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    true
  )
  const savedIds = useMemo(() => new Set(data?.product_ids ?? emptyIds), [data?.product_ids])
  return { savedIds, ...rest }
}

/**
 * Save or remove a product, keeping the cached id set in sync. The caller passes the
 * desired next state (`save`) so PUT/DELETE map directly instead of toggling blind.
 */
export function useSetWishlisted() {
  const invalidate = useStateInvalidation()
  const queryClient = useQueryClient()
  const viewer = useViewer()
  const wishlists = useGateway(wishlistGateway)

  return usePrivateMutation(
    {
      mutationFn: async ({ productId, save }: { productId: string; save: boolean }) => {
        if (save) {
          const wishlistId = await ensureWishlistId(viewer !== null && viewer !== ANONYMOUS_VIEWER, wishlists)
          return wishlists.save(wishlistId, productId)
        }
        const stored = getStoredWishlist()
        if (!stored) return { product_id: productId, saved: false }
        return wishlists.remove(stored.id, productId)
      },
      onSuccess: ({ product_id, saved }) => {
        queryClient.setQueryData<WishlistIdsResponse>(privateKey(viewer ?? ANONYMOUS_VIEWER, ...idsParts(getStoredWishlist()?.id ?? null)), (current) => ({
          product_ids: nextWishlistIds(current?.product_ids ?? [], product_id, saved),
        }))
        if (saved) void invalidateDomain(queryClient, WISHLIST_PRODUCTS)
        invalidate("wishlist")
      },
      scope: { id: "wishlist" },
    },
    true
  )
}
